import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Collapse,
  IconButton,
  CircularProgress,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotesIcon from '@mui/icons-material/Notes';
import useDailyPlanStore from '@/store/Employee/DailyPlanStore';
import { useAuth } from '@store/authStore';
import RitualDrawer from './RitualDrawer';
import TaskDecisionCard, { DecisionOption } from './TaskDecisionCard';
import { ctaSx, greeting } from './ritualStyles';

interface NewTaskRow {
  title: string;
  description: string;
}

type CarryDecision = { action: 'continue' | 'drop'; dropReason: string };

const CARRY_OPTIONS: DecisionOption[] = [
  { value: 'continue', label: 'Continue', tone: 'positive' },
  { value: 'drop', label: 'Drop', tone: 'negative' }
];

/** How long the completion moment lingers before the panel closes. */
const SUCCESS_MS = 900;

export interface PlanForTheDayPanelProps {
  open: boolean;
  /** Absent => blocking gate (no dismiss). */
  onClose?: () => void;
  onSubmitted?: () => void;
  /** Blocking mode: called after the completion moment, to release the gate. */
  onDone?: () => void;
  /** Blocking mode: the way out when submitting can't succeed. */
  onRetry?: () => void;
}

/**
 * Plan for the Day = the indirect punch-in.
 *
 * Carry-over tasks are triaged (Continue / Drop-with-reason), then at least one
 * active task must exist for today. Submitting records attendance.
 */
const PlanForTheDayPanel: React.FC<PlanForTheDayPanelProps> = ({
  open,
  onClose,
  onSubmitted,
  onDone,
  onRetry
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();

  const dayState = useDailyPlanStore((s) => s.dayState);
  const submitPlan = useDailyPlanStore((s) => s.submitPlan);
  const submitting = useDailyPlanStore((s) => s.submitting);
  const error = useDailyPlanStore((s) => s.error);

  // Memoized: a bare `dayState?.carryOverTasks || []` mints a fresh array on every
  // render while dayState is null, which would re-trigger the backfill effect below
  // on every render — and that effect sets state, so it would spin forever.
  const carryOver = useMemo(() => dayState?.carryOverTasks || [], [dayState?.carryOverTasks]);

  const [decisions, setDecisions] = useState<Record<number, CarryDecision>>({});
  const [tasks, setTasks] = useState<NewTaskRow[]>([{ title: '', description: '' }]);
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  // Cleared on unmount so a timer from a torn-down panel can't fire into its
  // replacement.
  const successTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(successTimer.current), []);

  // Every carried task must always have a COMPLETE decision object. The list can
  // arrive after mount, and a partial entry here would make `.trim()` throw and
  // blank the screen.
  useEffect(() => {
    setDecisions((prev) => {
      let changed = false;
      const next = { ...prev };
      carryOver.forEach((t) => {
        if (!next[t.id]) {
          next[t.id] = { action: 'continue', dropReason: '' };
          changed = true;
        }
      });
      // Returning `prev` unchanged lets React bail out of the re-render — without
      // this, a new object every time would keep the effect/render cycle alive.
      return changed ? next : prev;
    });
  }, [carryOver]);

  const setDecision = (id: number, action: 'continue' | 'drop') =>
    setDecisions((prev) => ({
      ...prev,
      [id]: { action, dropReason: prev[id]?.dropReason || '' }
    }));

  const setDropReason = (id: number, dropReason: string) =>
    setDecisions((prev) => ({
      ...prev,
      [id]: { action: prev[id]?.action || 'continue', dropReason }
    }));

  const updateTask = (idx: number, field: keyof NewTaskRow, value: string) =>
    setTasks((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  const addTaskRow = () => setTasks((prev) => [...prev, { title: '', description: '' }]);
  const removeTaskRow = (idx: number) =>
    setTasks((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const continueCount = useMemo(
    () => carryOver.filter((t) => decisions[t.id]?.action === 'continue').length,
    [carryOver, decisions]
  );
  const filledTaskCount = useMemo(() => tasks.filter((t) => t.title.trim()).length, [tasks]);

  const dropsValid = useMemo(
    () =>
      carryOver.every((t) => {
        const d = decisions[t.id];
        return d?.action !== 'drop' || (d.dropReason || '').trim().length > 0;
      }),
    [carryOver, decisions]
  );

  const hasActiveTask = filledTaskCount + continueCount >= 1;
  const isValid = dropsValid && hasActiveTask;

  // Progress is an affordance, not navigation: every carried task resolved, plus
  // "at least one thing planned".
  const progress = useMemo(() => {
    const resolvedCarry = carryOver.filter((t) => {
      const d = decisions[t.id];
      if (!d) return false;
      return d.action !== 'drop' || (d.dropReason || '').trim().length > 0;
    }).length;
    const done = resolvedCarry + (hasActiveTask ? 1 : 0);
    return (done / (carryOver.length + 1)) * 100;
  }, [carryOver, decisions, hasActiveTask]);

  const handleSubmit = async () => {
    const cleanTasks = tasks
      .filter((t) => t.title.trim())
      .map((t) => ({ title: t.title.trim(), description: t.description.trim() || undefined }));

    const carryOverPayload = carryOver.map((t) => {
      const d = decisions[t.id] || { action: 'continue' as const, dropReason: '' };
      return {
        originalTaskId: t.id,
        action: d.action,
        dropReason: d.action === 'drop' ? (d.dropReason || '').trim() : undefined
      };
    });

    const ok = await submitPlan({
      tasks: cleanTasks,
      carryOver: carryOverPayload,
      remarks: note.trim() || undefined
    });

    if (!ok) return;

    // Let the ritual land before the panel disappears.
    setSucceeded(true);
    successTimer.current = window.setTimeout(() => {
      onSubmitted?.();
      onClose?.();
      onDone?.();
    }, SUCCESS_MS);
  };

  const validationHint = !hasActiveTask
    ? 'Add at least one task, or continue something from yesterday, to start your day.'
    : !dropsValid
      ? 'Every dropped task needs a reason.'
      : '';

  const footerNote = validationHint ? (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
      {validationHint}
    </Typography>
  ) : (
    `${filledTaskCount + continueCount} task${filledTaskCount + continueCount !== 1 ? 's' : ''} planned` +
    (continueCount > 0 ? ` · ${continueCount} carried over` : '')
  );

  return (
    <RitualDrawer
      open={open}
      tone="plan"
      title={greeting(user?.firstName)}
      subtitle="Let's map out today, then get to it."
      progress={progress}
      onClose={onClose}
      footerNote={footerNote}
      success={
        succeeded
          ? {
              title: "You're all set.",
              subtitle: user?.firstName
                ? `Have a good one, ${user.firstName}.`
                : 'Have a good one.'
            }
          : null
      }
      recovery={
        error && onRetry ? (
          <Button onClick={onRetry} color="inherit" sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        ) : undefined
      }
      primaryAction={
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          sx={ctaSx(theme, 'success')}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Starting your day…' : 'Start My Day'}
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Carry-over triage ──────────────────────────────────── */}
      {carryOver.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              Picking up from yesterday
            </Typography>
            <Chip
              label={carryOver.length}
              size="small"
              sx={{ height: 20, fontWeight: 700, bgcolor: 'action.selected' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Still on your plate — continue it, or let it go.
          </Typography>

          {carryOver.map((task, i) => {
            const d = decisions[task.id];
            return (
              <TaskDecisionCard
                key={task.id}
                task={task}
                index={i}
                options={CARRY_OPTIONS}
                value={d?.action || 'continue'}
                onChange={(v) => setDecision(task.id, v as 'continue' | 'drop')}
                reason={d?.dropReason || ''}
                onReasonChange={(v) => setDropReason(task.id, v)}
                reasonRequiredFor="drop"
                reasonLabel="Why are you dropping this?"
                reasonPlaceholder='e.g. "No longer a priority"'
                groupLabel={`Decision for task: ${task.title}`}
              />
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            mb: 3.5,
            borderRadius: 2,
            bgcolor: `${theme.palette.success.main}12`
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: isDark ? 'success.light' : 'success.dark' }}
          >
            Clean slate — nothing carried over from yesterday.
          </Typography>
        </Box>
      )}

      {/* ── Today's tasks ──────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
        What are you tackling today?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add at least one thing — big or small.
      </Typography>

      {tasks.map((task, idx) => {
        const hasTitle = task.title.trim().length > 0;
        return (
          <Box
            key={idx}
            sx={{
              px: 1.75,
              py: hasTitle ? 1.5 : 1,
              mb: 1.25,
              borderRadius: 2,
              // Empty = a dashed affordance you're invited to fill. The moment a
              // title lands, it promotes into a solid card matching the carry-over
              // cards above, so both read as the same kind of object.
              border: '1.5px dashed',
              borderColor: hasTitle ? 'transparent' : `${theme.palette.primary.main}40`,
              background: hasTitle
                ? isDark
                  ? 'rgba(255,255,255,0.03)'
                  : theme.palette.background.paper
                : isDark
                  ? 'rgba(255,255,255,0.02)'
                  : `${theme.palette.primary.main}06`,
              boxShadow: hasTitle ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              transition: 'all 0.25s ease',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              '&:hover': hasTitle
                ? { '& .task-remove': { opacity: 1 } }
                : { borderColor: `${theme.palette.primary.main}80` }
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                variant="standard"
                autoFocus={idx === 0}
                placeholder="What will you work on?"
                value={task.title}
                onChange={(e) => updateTask(idx, 'title', e.target.value)}
                inputProps={{ 'aria-label': `Task ${idx + 1} title` }}
                InputProps={{ disableUnderline: !hasTitle }}
                sx={{ '& input': { fontWeight: 600, fontSize: '0.975rem' } }}
              />
              <Collapse in={hasTitle}>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  placeholder="Any details worth noting? (optional)"
                  value={task.description}
                  onChange={(e) => updateTask(idx, 'description', e.target.value)}
                  inputProps={{ 'aria-label': `Task ${idx + 1} details` }}
                  InputProps={{ disableUnderline: true }}
                  sx={{ mt: 0.5, '& input': { fontSize: '0.875rem' } }}
                />
              </Collapse>
            </Box>
            {tasks.length > 1 && (
              <IconButton
                className="task-remove"
                aria-label={`Remove task ${idx + 1}`}
                onClick={() => removeTaskRow(idx)}
                size="small"
                sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.2s ease' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}

      <Button
        startIcon={<AddIcon />}
        onClick={addTaskRow}
        sx={{ textTransform: 'none', fontWeight: 600, mb: 1 }}
      >
        Add another task
      </Button>

      {/* ── Optional note ──────────────────────────────────────── */}
      <Box sx={{ mt: 2 }}>
        {!noteOpen ? (
          <Button
            startIcon={<NotesIcon />}
            onClick={() => setNoteOpen(true)}
            color="inherit"
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Add a note about today
          </Button>
        ) : (
          <Collapse in>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              autoFocus
              label="Note for today (optional)"
              placeholder="Anything else worth flagging?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Collapse>
        )}
      </Box>
    </RitualDrawer>
  );
};

export default PlanForTheDayPanel;
